/**
 * TickerStrip — live-updating horizontal strip of ticker items shown
 * directly below KpiGrid on the homepage.
 *
 * Features:
 *   - SSR-seeded initial items (`initialItems` prop from +data.ts)
 *   - Live updates via the `/sse/ticker?locale=…` stream
 *   - Seamless CSS marquee animation (two strips, reduced-motion aware)
 *   - Full RTL support for Arabic (`locale === "ar"`)
 *   - WCAG 2.1 AA: 44×44 touch targets, aria-live, keyboard pause
 */

import { useEffect, useReducer, useRef, useState } from "react";
import type { TickerItemOut } from "@hantavirus/shared";
import type { TranslateFn } from "#/lib/i18n.js";
import { useSSE } from "#/hooks/useSSE.js";
import { SSEConnectionBanner } from "./SSEConnectionBanner.js";
import styles from "./TickerStrip.module.css";

/* ---------------------------------------------------------------
 * Public props
 * --------------------------------------------------------------- */

export interface TickerStripProps {
  initialItems: TickerItemOut[];
  locale: string;
  t: TranslateFn;
}

/* ---------------------------------------------------------------
 * Constants
 * --------------------------------------------------------------- */

const MAX_ITEMS = 12;
const MAX_ITEMS_REDUCED_MOTION = 6;

/** Map severity to its CSS custom property. Reuses the same vars as
 *  BreakingBanner so no new design tokens are introduced. */
const SEVERITY_COLOR: Record<TickerItemOut["severity"], string> = {
  urgent: "var(--color-accent-emergency)",
  caution: "var(--color-accent-warning)",
  info: "var(--color-accent-info)",
};

/* ---------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------- */

/**
 * Normalize a raw SSE payload whose date fields arrive as ISO strings
 * (JSON.parse doesn't reconstruct Date objects). initialItems from SSR
 * already carry real Date instances, so this is a no-op for them.
 */
function normalizeItem(raw: TickerItemOut): TickerItemOut {
  return {
    ...raw,
    publishedAt:
      raw.publishedAt instanceof Date
        ? raw.publishedAt
        : new Date(raw.publishedAt as unknown as string),
    expiresAt:
      raw.expiresAt == null
        ? null
        : raw.expiresAt instanceof Date
          ? raw.expiresAt
          : new Date(raw.expiresAt as unknown as string),
  };
}

function isExpired(item: TickerItemOut): boolean {
  if (!item.expiresAt) return false;
  const expires =
    item.expiresAt instanceof Date
      ? item.expiresAt
      : new Date(item.expiresAt as unknown as string);
  return expires.getTime() < Date.now();
}

function evictExpired(items: TickerItemOut[]): TickerItemOut[] {
  return items.filter((i) => !isExpired(i));
}

/** Prepend item to head, deduplicate by id, evict expired, cap at MAX. */
function addToHead(
  items: TickerItemOut[],
  item: TickerItemOut,
): TickerItemOut[] {
  const deduped = [item, ...items.filter((i) => i.id !== item.id)];
  return evictExpired(deduped).slice(0, MAX_ITEMS);
}

/**
 * Locale-aware relative time using built-in `Intl.RelativeTimeFormat`.
 * No external library required.
 */
function relativeTime(date: Date | string, locale: string): string {
  const d = date instanceof Date ? date : new Date(date);
  const diffSecs = Math.floor((Date.now() - d.getTime()) / 1000);

  // Clamp to positive (future dates treated as "just now")
  if (diffSecs < 5) {
    // Use a static string for very-fresh items to keep aria-live quiet
    return locale === "ar" ? "الآن" : locale === "fr" ? "à l'instant" : "just now";
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (diffSecs < 60) return rtf.format(-diffSecs, "second");
  if (diffSecs < 3_600) return rtf.format(-Math.floor(diffSecs / 60), "minute");
  if (diffSecs < 86_400) return rtf.format(-Math.floor(diffSecs / 3_600), "hour");
  return rtf.format(-Math.floor(diffSecs / 86_400), "day");
}

/* ---------------------------------------------------------------
 * Reduced-motion hook (SSR-safe)
 * --------------------------------------------------------------- */

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return reduced;
}

/* ---------------------------------------------------------------
 * Reducer
 * --------------------------------------------------------------- */

type State = { items: TickerItemOut[] };

type Action =
  | { type: "SNAPSHOT"; item: TickerItemOut }
  | { type: "CREATED"; item: TickerItemOut }
  | { type: "UPDATED"; item: TickerItemOut }
  | { type: "EXPIRED"; item: TickerItemOut };

function reducer(state: State, action: Action): State {
  const item = normalizeItem(action.item);

  switch (action.type) {
    case "SNAPSHOT": {
      // Snapshot events arrive when SSE connects. Skip expired items and
      // items already in state (from SSR initialItems).
      if (isExpired(item)) return state;
      if (state.items.some((i) => i.id === item.id)) return state;
      return { items: [...state.items, item].slice(0, MAX_ITEMS) };
    }
    case "CREATED":
    case "UPDATED":
      return { items: addToHead(state.items, item) };
    case "EXPIRED":
      return { items: state.items.filter((i) => i.id !== item.id) };
    default:
      return state;
  }
}

/* ---------------------------------------------------------------
 * Chip component
 * --------------------------------------------------------------- */

interface ChipProps {
  item: TickerItemOut;
  locale: string;
}

function Chip({ item, locale }: ChipProps) {
  const relTime = relativeTime(item.publishedAt, locale);
  const dotColor = SEVERITY_COLOR[item.severity];

  const inner = (
    /* min-h-[44px] + px-4 py-3 achieves the 44px touch target on the
     * parent container — NOT on a nested interactive element. */
    <div className="flex items-center gap-2 px-4 py-3 min-h-[44px] whitespace-nowrap">
      {/* Severity dot — aria-hidden; severity is conveyed via context */}
      <span
        aria-hidden="true"
        className="h-2 w-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: dotColor }}
      />
      {/* Relative publish time */}
      <span className="text-xs font-mono text-muted-foreground me-1 flex-shrink-0">
        {relTime}
      </span>
      {/* Headline — truncated on mobile, 2 lines on large screens */}
      <span className="text-sm text-foreground line-clamp-1 @lg:line-clamp-2">
        {item.text}
      </span>
    </div>
  );

  if (item.link) {
    return (
      <div className={styles.chip}>
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-colors duration-150"
        >
          {inner}
        </a>
      </div>
    );
  }

  return <div className={styles.chip}>{inner}</div>;
}

/* ---------------------------------------------------------------
 * TickerStrip (main export)
 * --------------------------------------------------------------- */

export function TickerStrip({ initialItems, locale, t }: TickerStripProps) {
  const isRtl = locale === "ar";
  const prefersReducedMotion = usePrefersReducedMotion();

  /* Filter expired items from SSR data on mount */
  const [state, dispatch] = useReducer(reducer, {
    items: evictExpired(initialItems.map(normalizeItem)).slice(0, MAX_ITEMS),
  });

  /* SSE live updates */
  const { status, reconnectCount, retry } = useSSE<
    "ticker.snapshot" | "ticker.created" | "ticker.updated" | "ticker.expired",
    TickerItemOut
  >(
    `/sse/ticker?locale=${locale}`,
    {
      "ticker.snapshot": (data) =>
        dispatch({ type: "SNAPSHOT", item: data }),
      "ticker.created": (data) =>
        dispatch({ type: "CREATED", item: data }),
      "ticker.updated": (data) =>
        dispatch({ type: "UPDATED", item: data }),
      "ticker.expired": (data) =>
        dispatch({ type: "EXPIRED", item: data }),
    },
  );

  /* Items to display — cap at 6 in reduced-motion mode */
  const visibleItems = prefersReducedMotion
    ? state.items.slice(0, MAX_ITEMS_REDUCED_MOTION)
    : state.items;

  /* Track ref for aria-live region; we announce when new items appear */
  const prevCountRef = useRef(visibleItems.length);
  useEffect(() => {
    prevCountRef.current = visibleItems.length;
  });

  /* Track CSS class — combines base class with RTL variant */
  const trackClass = [
    styles.track,
    !prefersReducedMotion && isRtl ? styles.trackRtl : "",
  ]
    .filter(Boolean)
    .join(" ");

  /* ------ Empty state ------ */
  if (visibleItems.length === 0) {
    return (
      <>
        <SSEConnectionBanner
          status={status}
          reconnectCount={reconnectCount}
          onRetry={retry}
          t={t}
        />
        <section
          role="marquee"
          aria-live="polite"
          aria-label={t("ticker.label")}
          className="border-y border-border bg-card"
        >
          <div className="flex items-center px-4 py-3 min-h-[44px] text-sm text-muted-foreground">
            {t("ticker.empty")}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SSEConnectionBanner
        status={status}
        reconnectCount={reconnectCount}
        onRetry={retry}
        t={t}
      />
      <section
        role="marquee"
        aria-live="polite"
        aria-label={t("ticker.label")}
        className="border-y border-border bg-card"
      >
      <div className={styles.strip}>
        <div className={trackClass}>
          {/* Visible strip */}
          <div className={styles.inner}>
            {visibleItems.map((item) => (
              <Chip key={item.id} item={item} locale={locale} />
            ))}
          </div>

          {/* Duplicate strip for seamless loop — hidden from screen readers.
              Not rendered when reduced motion is preferred (static display). */}
          {!prefersReducedMotion && (
            <div className={styles.inner} aria-hidden="true">
              {visibleItems.map((item) => (
                <Chip key={`dup-${item.id}`} item={item} locale={locale} />
              ))}
            </div>
          )}
        </div>
      </div>
      </section>
    </>
  );
}

export default TickerStrip;
